import {
  getHubSpotStatus,
  triggerProjectSync,
  updateProjectHubSpotConfig,
} from '@api/hubspot.js';
import { getTemplates } from '@api/projects.js';
import toast from '@components/Common/Toast.js';
import Toggle from '@components/Common/Toggle.js';
import Loader from '@components/Loading/Loader.js';
import Modal from '@components/Modals/Modal.js';
import {
  CalendarIcon,
  FileIcon,
  FolderIcon,
  HubSpotIcon,
  MailIcon,
  PackageIcon,
  ShoppingCartIcon,
  UsersIcon,
} from '@icons/index.js';
import { LoadedFolderIds } from '@states/Projects.js';
import { UIDiv, UIInput, UIText } from '@ui';
import { addProject } from '@utils/projectUtils';

// Template icon mapping to icon components
const TEMPLATE_ICON_COMPONENTS = {
  folder: FolderIcon,
  users: UsersIcon,
  'shopping-cart': ShoppingCartIcon,
  calendar: CalendarIcon,
  mail: MailIcon,
  file: FileIcon,
  package: PackageIcon,
};

export default class ProjectCreateModal extends Modal {
  constructor() {
    super(i18next.t('pages.projects.modals.createProject.title'), {
      showCancel: true,
      showConfirm: true,
      confirmText: i18next.t('common.create'),
      cancelText: i18next.t('common.cancel'),
      modalContentClass: '!max-w-xl',
      contentContainerClass: 'p-4',
      handleOnAction: () =>
        this.createProject(this.inputField.getValue(), this.selectedTemplateId),
    });

    this.selectedTemplateId = 'projects';
    this.templates = [];
    this.templateCards = new Map();
    this.isCreating = false; // Loading state for create operation
    this.loader = null; // Loading spinner element

    // HubSpot sync state
    this.hubspotConnected = false;
    this.hubspotSyncEnabled = false;
    this.hubspotSyncConfig = {
      contacts: { enabled: true },
    };
  }

  createModal() {
    const modal = super.createModal();

    // Create loading spinner for inside the button
    this.loader = new Loader(
      'spinner w-4 h-4 border-2 border-t-2 rounded-full animate-spin border-t-white border-gray-300',
      true,
    );
    this.loader.setStyle('display', ['none']); // Hidden by default

    // Store original button text for restoration
    if (this.actionButton) {
      this.originalButtonText = this.actionButton.dom.textContent;
    }

    // Template selection section
    const templateSection = new UIDiv();
    templateSection.setClass('mb-4');

    const templateLabel = new UIText(
      i18next.t('pages.projects.modals.createProject.selectTemplate'),
    );
    templateLabel.setClass('text-sm font-medium text-gray-700 mb-2 block');
    templateSection.add(templateLabel);

    // Template grid container
    this.templateGrid = new UIDiv();
    this.templateGrid.setClass('grid grid-cols-2 gap-2 mb-4');

    // Add loading placeholder
    const loadingText = new UIText(
      i18next.t('pages.projects.modals.createProject.loadingTemplates'),
    );
    loadingText.setClass('text-sm text-gray-500 col-span-2 text-center py-4');
    this.templateGrid.add(loadingText);

    templateSection.add(this.templateGrid);
    // this.contentContainer.add(templateSection);

    // Project name input section
    const nameSection = new UIDiv();
    nameSection.setClass('mb-2');

    const nameLabel = new UIText(
      i18next.t('pages.projects.modals.createProject.projectName'),
    );
    nameLabel.setClass('text-sm font-medium text-gray-700 mb-2 block');
    nameSection.add(nameLabel);

    this.inputField = new UIInput('').setPlaceholder(
      i18next.t('pages.projects.modals.createProject.placeholder'),
    );
    this.inputField.setClass(
      'w-full px-3 cursor-text py-2.5 bg-[#F2F5F9] border-0 rounded-xl !shadow-none focus:outline-none focus:bg-white placeholder-[#787777] focus:ring-2 focus:ring-[var(--o-hive-primary-80)]',
    );

    this.inputField.onKeyDown(async e => {
      if (e.key === 'Enter' && !e.shiftKey && !this.isCreating) {
        e.preventDefault();
        const value = this.inputField.getValue();
        const success = await this.createProject(
          value,
          this.selectedTemplateId,
        );
        if (success) this.close();
      }
    });

    nameSection.add(this.inputField);
    this.contentContainer.add(nameSection);

    // HubSpot sync section (hidden by default)
    this.hubspotSection = this.#createHubSpotSyncSection();
    this.hubspotSection.setClass('hidden mb-4');
    this.contentContainer.add(this.hubspotSection);

    // Load templates and HubSpot status
    this.loadTemplates();
    this.#loadHubSpotStatus();

    requestAnimationFrame(() => {
      this.inputField.dom.focus();
    });

    return modal;
  }

  async #loadHubSpotStatus() {
    try {
      const response = await getHubSpotStatus();
      this.hubspotConnected = response.connected;
      this.hubspotPortalName = response.portalName;
      // Update the sync section if CRM is already selected
      if (this.selectedTemplateId === 'crm') {
        this.#updateHubSpotSectionVisibility();
      }
    } catch (error) {
      console.error('Failed to load HubSpot status:', error);
      this.hubspotConnected = false;
    }
  }

  #createHubSpotSyncSection() {
    const section = new UIDiv();
    section.setClass(
      'border border-[#ff7a59]/30 rounded-lg p-4 bg-[#ff7a59]/5',
    );

    // Header with icon and toggle
    const header = new UIDiv();
    header.setClass('flex items-center justify-between mb-3');

    const titleContainer = new UIDiv();
    titleContainer.setClass('flex items-center gap-2');

    const hubspotIcon = new HubSpotIcon({ size: 20 });
    titleContainer.add(hubspotIcon);

    const title = new UIText(
      i18next.t('pages.projects.modals.createProject.syncHubSpot'),
    );
    title.setClass('text-sm font-medium text-gray-900');
    titleContainer.add(title);

    header.add(titleContainer);

    // Toggle for enabling sync
    this.hubspotToggle = new Toggle({
      initialState: this.hubspotSyncEnabled,
      onChange: enabled => {
        this.hubspotSyncEnabled = enabled;
        this.#updateHubSpotOptionsVisibility();
      },
    });
    header.add(this.hubspotToggle);

    section.add(header);

    // Portal info
    this.portalInfo = new UIDiv();
    this.portalInfo.setClass('text-xs text-gray-500 mb-3');
    section.add(this.portalInfo);

    // Options container (hidden when toggle is off)
    this.hubspotOptionsContainer = new UIDiv();
    this.hubspotOptionsContainer.setClass('hidden space-y-3');

    // Info text about what will be synced
    const infoText = new UIText(
      i18next.t('pages.projects.modals.createProject.hubspot.syncInfo'),
    );
    infoText.setClass('text-sm text-gray-600');
    this.hubspotOptionsContainer.add(infoText);

    section.add(this.hubspotOptionsContainer);

    return section;
  }

  #updateHubSpotSectionVisibility() {
    if (this.selectedTemplateId === 'crm' && this.hubspotConnected) {
      this.hubspotSection.removeClass('hidden');
      this.portalInfo.dom.textContent = i18next.t(
        'pages.projects.modals.createProject.hubspot.connectedTo',
        { portalName: this.hubspotPortalName || 'HubSpot' },
      );
    } else {
      this.hubspotSection.addClass('hidden');
    }
  }

  #updateHubSpotOptionsVisibility() {
    if (this.hubspotSyncEnabled) {
      this.hubspotOptionsContainer.removeClass('hidden');
    } else {
      this.hubspotOptionsContainer.addClass('hidden');
    }
  }

  async loadTemplates() {
    try {
      const response = await getTemplates();
      if (response.status === 'success' && response.response) {
        this.templates = response.response;
        this.renderTemplates();
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      // Fall back to default template
      this.templates = [
        {
          id: 'projects',
          name: i18next.t('pages.projects.templates.projects.name'),
          description: i18next.t(
            'pages.projects.templates.projects.description',
          ),
          icon: 'projects',
        },
      ];
      this.renderTemplates();
    }
  }

  renderTemplates() {
    this.templateGrid.clear();
    this.templateCards.clear();

    this.templates.forEach(template => {
      const card = this.createTemplateCard(template);
      this.templateCards.set(template.id, card);
      this.templateGrid.add(card);
    });

    // Select the default template
    this.selectTemplate('projects');
  }

  createTemplateCard(template) {
    const card = new UIDiv();
    // Use data-state for styling - default state is 'default'
    card.setClass(
      'template-card flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ' +
        'data-[state=default]:border-gray-200 data-[state=default]:hover:border-[var(--o-hive-primary-60)] data-[state=default]:hover:bg-gray-50 ' +
        'data-[state=selected]:border-[var(--o-hive-primary)] data-[state=selected]:bg-[var(--o-hive-primary-10)]',
    );
    card.dom.dataset.templateId = template.id;
    card.dom.dataset.state = 'default';

    // Icon container with data-state styling
    const iconContainer = new UIDiv();
    iconContainer.setClass(
      'template-icon flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all ' +
        'data-[state=default]:bg-gray-100 data-[state=default]:text-gray-600 ' +
        'data-[state=selected]:bg-[var(--o-hive-primary-20)] data-[state=selected]:text-[var(--o-hive-primary)]',
    );
    iconContainer.dom.dataset.state = 'default';

    // Use icon component instead of inline SVG
    const IconComponent =
      TEMPLATE_ICON_COMPONENTS[template.icon] ||
      TEMPLATE_ICON_COMPONENTS.folder;
    const icon = new IconComponent({ size: 20, color: 'none' });
    iconContainer.add(icon);
    card.add(iconContainer);

    // Text container
    const textContainer = new UIDiv();
    textContainer.setClass('flex-1 min-w-0');

    const name = new UIText(
      i18next.t(`pages.projects.templates.${template.id}.name`, {
        defaultValue: template.name,
      }),
    );
    name.setClass('text-sm font-medium text-gray-900 truncate');
    textContainer.add(name);

    const description = new UIText(
      i18next.t(`pages.projects.templates.${template.id}.description`, {
        defaultValue: template.description,
      }),
    );
    description.setClass('text-xs text-gray-500 line-clamp-2 mt-0.5');
    textContainer.add(description);

    card.add(textContainer);

    // Click handler
    card.onClick(() => {
      this.selectTemplate(template.id);
    });

    return card;
  }

  selectTemplate(templateId) {
    this.selectedTemplateId = templateId;

    // Update visual selection using data-state attribute
    this.templateCards.forEach((card, id) => {
      const state = id === templateId ? 'selected' : 'default';
      card.dom.dataset.state = state;
      // Update icon container state
      const iconContainer = card.dom.querySelector('.template-icon');
      if (iconContainer) {
        iconContainer.dataset.state = state;
      }
    });

    // Show/hide HubSpot sync section for CRM template
    this.#updateHubSpotSectionVisibility();
  }

  async createProject(value, templateId) {
    // Prevent multiple simultaneous create requests
    if (this.isCreating) {
      return false;
    }

    try {
      if (!value || value.trim() === '') {
        toast.warning(
          i18next.t('pages.projects.modals.createProject.errorEmpty'),
        );
        this.inputField.focus();
        return false;
      }

      // Set loading state and disable button
      this.isCreating = true;
      this.disabledActionButton(true);

      // Replace button text with loader
      if (this.actionButton && this.loader) {
        this.actionButton.clear(); // Clear existing child elements
        this.actionButton.dom.textContent = ''; // Clear text content
        this.actionButton.add(this.loader); // Add loader inside button
        this.loader.show();
      }

      // Create project via API with selected template
      const project = await addProject(value.trim(), templateId);

      // If HubSpot sync is enabled for CRM projects, save the config
      if (
        templateId === 'crm' &&
        this.hubspotSyncEnabled &&
        this.hubspotConnected &&
        project?.id
      ) {
        try {
          await updateProjectHubSpotConfig(project.id, {
            enabled: true,
            portalId: null, // Will be filled from user's integration
            portalName: this.hubspotPortalName,
            syncConfig: {
              entities: this.hubspotSyncConfig,
              fieldMapping: {}, // Uses hardcoded field mapping
            },
          });

          // Trigger initial sync to import contacts immediately
          toast.info(
            i18next.t('pages.projects.modals.createProject.hubspot.syncing'),
          );

          try {
            await triggerProjectSync(project.id);

            // Clear cache for this project so fresh data is fetched when user opens it
            const currentLoadedIds = LoadedFolderIds.loadedFolderIds || [];
            LoadedFolderIds.setLoadedFolderIds(
              currentLoadedIds.filter(id => id !== project.id),
            );

            toast.success(
              i18next.t(
                'pages.projects.modals.createProject.hubspot.syncComplete',
              ),
            );
          } catch (syncError) {
            console.error('Initial HubSpot sync failed:', syncError);
            toast.warning(
              i18next.t(
                'pages.projects.modals.createProject.hubspot.syncFailed',
              ),
            );
          }
        } catch (hubspotError) {
          console.error('Failed to configure HubSpot sync:', hubspotError);
          // Don't fail the project creation, just warn
          toast.warning(
            i18next.t(
              'pages.projects.modals.createProject.hubspot.syncConfigFailed',
            ),
          );
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error(
        i18next.t('pages.projects.modals.createProject.errorCreate', {
          error: error.message,
        }),
      );
      return false;
    } finally {
      // Always reset loading state
      this.isCreating = false;
      this.disabledActionButton(false);

      // Restore button text and hide loader
      if (this.actionButton) {
        this.actionButton.clear(); // Clear loader
        this.actionButton.dom.textContent =
          this.originalButtonText || i18next.t('common.create');
      }
    }
  }
}
